import { useEffect } from "react";
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import weekday from "dayjs/plugin/weekday";
import updateLocale from "dayjs/plugin/updateLocale";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import duration from "dayjs/plugin/duration";

// Store entities
import { getDateState } from "../store/date/date.selectors";
import { saveSelectedTimeZone, setLocalTimeZone } from "../store/date/date.thunks";
import { getUserState } from "../store/user/user.selectors";

// Dayjs plugins setup
dayjs.extend(utc)
dayjs.extend(timezone);
dayjs.extend(duration);
dayjs.extend(weekday);
dayjs.extend(updateLocale)
dayjs.updateLocale('en', {
  weekStart: 1
});

/**
 * Custom React Hook for tracking window height changes.
 * @returns {void}
 */
export const useDate = (): void => {

  const { timezonesOptions, localTimezone, selectedTimezone } = useSelector(getDateState);
  const { user } = useSelector(getUserState);

  const dispatch = useDispatch<any>();

  /**
   * Change the user's current timezone based on their browser's timezone offset
   */
  const changeUserCurrentTimezone = (timeZone: string): void => {
    dispatch(saveSelectedTimeZone({ timeZone }));
  }

  /**
   * Sets the user's current timezone based on their browser's timezone offset
   */
  const setUserCurrentTimezone = (): void => {
    if (!timezonesOptions.length) return;


    try {
      let userTimeZoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // First we look for an exact match of the name (case-insensitive)
      let timezoneOption = timezonesOptions.find(
        option => option.value.toLowerCase() === userTimeZoneName.toLowerCase()
      );

      // Special handling for Ukraine (Kiev/Kyiv variants)
      if (!timezoneOption && (userTimeZoneName === 'Europe/Kiev' || userTimeZoneName === 'Europe/Kyiv')) {
        timezoneOption = timezonesOptions.find(
          option => option.value === 'Europe/Kyiv' || option.value === 'Europe/Kiev'
        );
      }

      // If no exact match is found - search by offset
      if (!timezoneOption) {
        const userTimezoneOffset = Math.round(-(new Date().getTimezoneOffset()));

        const foundTimezones = timezonesOptions.filter(option => option.offset === userTimezoneOffset);

        // Priority for Europe/* timezones when searching by offset
        timezoneOption = foundTimezones.find(option => option.value.startsWith('Europe/'))
          || foundTimezones[0];
      }

      // Fallback on first timezone from list
      if (!timezoneOption) {
        timezoneOption = timezonesOptions[0];
      }

      dispatch(setLocalTimeZone({ timeZone: timezoneOption.value }));
    } catch (error) {
      console.error('Failed to detect timezone:', error);
      dispatch(setLocalTimeZone({ timeZone: timezonesOptions[0]?.value || 'UTC' }));
    }
  }

  useEffect(() => {

    // If user has saved timezone on server - use it
    if (user?.timezone && !selectedTimezone) {
      changeUserCurrentTimezone(user.timezone);
      return;
    }

    // If no selected timezone - do nothing (will be set from user or browser)
    if (!selectedTimezone) return;

    // Only save if different from local
    if (localTimezone !== selectedTimezone) {
      changeUserCurrentTimezone(selectedTimezone);
    }
  }, [selectedTimezone, user, localTimezone]);

  useEffect(() => {
    setUserCurrentTimezone();
  }, [timezonesOptions]);
};
